import "./App.css";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useApp } from "./hooks/useApp";
import { Box } from "@mui/system";
import { useEffect, lazy, Suspense } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase/config";
import { ThemeProvider } from "@mui/material";
import createCustomTheme from "./components/theme/theme";
import Toast from "./components/Common/Toast";
import {
  canUserViewAsset,
  hasMatchingPrivateDomain,
} from "./Utils/assetVisibility";

import {
  Login,
  Vtec,
  Admin,
  PartnerLogin,
  Composable,
  FactoryDirect,
  CommonDataModel,
  PartnerAdmin,
  ManageAssets,
  PrivateAccounts,
  Flow,
  Uns,
  ManageFilters,
  Seedvault,
  Copy,
  Proveit,
  ProveitEdit,
  ProveitSim,
  AssetPage,
  ModelScene,
  Instances,
  AiFeatures,
  Assets,
  FeatureForm,
  AssetsForm,
  Factories,
  FactoryForm,
  VerifyEmail,
  Experience,
  Architecture,
  ArchitectureEditor,
  BubblesManagement,
  HannoverDirect
} from "./pages";  
import BubbleEditorPage from "./pages/change-bubbles/bubbles-form";
const ExperienceStoriesPage = lazy(() => import("./pages/experience/stories/Index").then(m => ({ default: m.ExperienceStoriesPage })));
const StoryAnalyticsPage = lazy(() => import("./pages/experience/stories/StoryAnalytics").then(m => ({ default: m.StoryAnalyticsPage })));

function App() {
  const { authIsReady, user } = useAuth();
  const {
    setVtecAssets,
    combinedAssets,
    partnerTheme,
    setPartnerTheme,
    setSvgLogo,
    setSelectedFilter,
    filters,
  } = useApp();

  // useEffect to re-render when 'user' changes
  useEffect(() => {
    if (user) {
      httpsCallable(functions, "identify")().then((result) => {});
      if (user.privateAccount) {
        const currentTheme = localStorage.getItem("theme");
        const logo = localStorage.getItem("logo");
        if (!currentTheme || !logo) {
          if (user.privateAccount.theme) {
            setPartnerTheme(user.privateAccount.theme);
            localStorage.setItem(
              "theme",
              JSON.stringify(user.privateAccount.theme)
            );
          }
          if (user.privateAccount.svg) {
            setSvgLogo(user.privateAccount.svg);
            localStorage.setItem("logo", user.privateAccount.svg);
          }
        }
      }
    }
  }, [user, setPartnerTheme, setSvgLogo]);
  useEffect(() => {
    if (filters) {
      if (user?.defaultView) {
        const defaultView = filters.find((x) => x.id === user.defaultView);
        setSelectedFilter(defaultView);
      } else {
        const defaultView = filters.find((x) => x.isAdminDefault === true);
        setSelectedFilter(defaultView);
      }
    }
  }, [filters, user, setSelectedFilter]);
  const sorter = (assets) => {
    return assets
      ? assets.sort((a, b) => {
          const aIsDomainShared = hasMatchingPrivateDomain(a, user);
          const bIsDomainShared = hasMatchingPrivateDomain(b, user);
          if (aIsDomainShared !== bIsDomainShared) {
            return bIsDomainShared - aIsDomainShared;
          }
          return 0;
        })
      : null;
  };
  useEffect(() => {
    if (user && combinedAssets) {
      const assetsAfterFilter = combinedAssets.filter((asset) =>
        canUserViewAsset(asset, user) && !asset.isHidden
      );
      const sortedAssets = sorter(assetsAfterFilter);
      setVtecAssets(sortedAssets);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, combinedAssets, setVtecAssets]);

  const theme = createCustomTheme(
    partnerTheme.primaryColor,
    partnerTheme.bgColor,
    partnerTheme.highlight
  );
  useEffect(() => {
    const currentTheme = createCustomTheme(
      partnerTheme.primaryColor,
      partnerTheme.bgColor,
      partnerTheme.highlight
    );
    const root = document.documentElement;
    root?.style.setProperty(
      "--gradient-default-nav",
      `linear-gradient(to right, ${currentTheme.palette.primary.main}, ${currentTheme.palette.secondary.main})`
    );
    root?.style.setProperty(
      "--gradient-default",
      `linear-gradient(to left,${currentTheme.palette.primary.main}, ${currentTheme.palette.secondary.main})`
    );
    root?.style.setProperty(
      "--gradient-default-bottom",
      `linear-gradient(to bottom, ${currentTheme.palette.primary.main}, ${currentTheme.palette.secondary.main})`
    );
    root?.style.setProperty(
      "--radial-gradient",
      `radial-gradient(circle at 50% 50%, ${currentTheme.palette.secondary.main} 0%, transparent 90% ), ${currentTheme.palette.primary.main}`
    );
    root?.style.setProperty(
      "--text-white",
      `${currentTheme.palette.customColors.white}`
    );
  }, [partnerTheme]);
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: "100vh",
        }}
      >
        {authIsReady && (
          <BrowserRouter>
            <Routes>
              <Route
                exact
                path="/"
                element={
                  user && user.approved ? (
                    <Vtec />
                  ) : user ? (
                    <Navigate to="/login" replace={true} />
                  ) : (
                    <Navigate to="/login" replace={true} />
                  )
                }
              ></Route>
              <Route
                exact
                path="/login"
                element={
                  !user || (user && !user.approved) ? (
                    <Login />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>
             
              <Route
                exact
                path="/seedvault"
                element={user?.admin ? <Seedvault /> : <Copy />}
              ></Route>
               
              <Route
                exact
                path="/experience"
                element={user && user.approved  ? <Experience /> : (
                    <Navigate to="/" replace={true} />
                  )}
              ></Route>
              <Route
                exact
                path="/manage-assets"
                element={
                  user && user.approved ? (
                    <ManageAssets />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/experience/interactive-ra"
                element={
                  user && user.approved ? (
                    <Architecture />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/architecture-editor"
                element={
                  user?.admin ? (
                    <ArchitectureEditor />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/experience/stories"
                element={
                    <Suspense fallback={null}><ExperienceStoriesPage /></Suspense>
                }
              ></Route>

              <Route
                exact
                path="/experience/stories/nda"
                element={
                  user && user.approved ? (
                    <Suspense fallback={null}><ExperienceStoriesPage /></Suspense>
                  ) : (
                    <Navigate to="/login" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/experience/stories/usecases"
                element={
                    <Suspense fallback={null}><ExperienceStoriesPage /></Suspense>
                }
              ></Route>

              <Route
                exact
                path="/experience/stories/nda/usecases"
                element={
                  user && user.approved ? (
                    <Suspense fallback={null}><ExperienceStoriesPage /></Suspense>
                  ) : (
                    <Navigate to="/login" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/experience/stories/_admin"
                element={
                  user?.admin ? (
                    <Suspense fallback={null}><StoryAnalyticsPage /></Suspense>
                  ) : (
                    <Navigate to="/experience/stories" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/experience/stories/nda/_admin"
                element={
                  <Navigate to="/experience/stories/_admin" replace={true} />
                }
              ></Route>

              <Route
                exact
                path="/verified-email"
                element={
                  user && user.approved ? (
                    <VerifyEmail />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>

              <Route
                exact
                path="/admin"
                element={
                  user?.admin ? <Admin /> : <Navigate to="/" replace={true} />
                }
              ></Route>
              <Route
                exact
                path="/users"
                element={
                  user?.admin ? (
                    <PartnerAdmin />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>
              <Route
                exact
                path="/partnerlogin"
                element={
                  !user ? <PartnerLogin /> : <Navigate to="/" replace={true} />
                }
              ></Route>
              <Route path="/composable" element={<Composable />}></Route>
              <Route path="/composable/:param" element={<Composable />}></Route>
              <Route
                path="/factory/:account/:asset"
                element={<FactoryDirect />}
              ></Route>
              <Route
                exact
                path="/hannover"
                element={<HannoverDirect />}
              ></Route>
              <Route
                exact
                path="/commondatamodel"
                element={<CommonDataModel />}
              ></Route>

              {/* Base path for AI features */}
              <Route
                path="/ai-features"
                element={
                  user && user.approved ? (
                    <AiFeatures />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              {/* Create new feature */}
              <Route
                path="/ai-features/create"
                element={
                  user && user.approved ? (
                    <FeatureForm mode="create" />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              {/* Edit existing feature */}
              <Route
                path="/ai-features/edit/:id"
                element={
                  user && user.approved ? (
                    <FeatureForm mode="edit" />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              <Route
                exact
                path="/change-bubbles"
                element={
                    <BubblesManagement />
                }
              ></Route>

              {/* Create new feature */}
              <Route
                path="/change-bubbles/create"
                element={
                  user && user.approved ? (
                    <BubbleEditorPage mode="create" />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              {/* Edit existing feature */}
              <Route
                path="/change-bubbles/edit/:id"
                element={
                  user && user.approved ? (
                    <BubbleEditorPage mode="edit" />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              {/* Base path for AI features */}
              <Route
                path="/assets"
                element={
                  user && user.approved ? (
                    <Assets />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              />

              {/* Create new feature */}
              <Route
                path="/assets/create"
                element={
                  user && user.approved ? (
                    <AssetsForm />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              />

              {/* Edit existing feature */}
              <Route
                path="/assets/edit/:id"
                element={
                  user && user.approved ? (
                    <AssetsForm />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              />

              {/* Base path for AI features */}
              <Route
               path="/factories"
                element={
                  user && user.approved ? (
                    <Factories />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              />

              {/* Create new feature */}
              <Route
                path="/factories/create"
                element={
                  user && user.approved ? (
                    <FactoryForm />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              />

              {/* Edit existing feature */}
              <Route
                path="/factories/edit/:id"
                element={
                  user && user.approved ? (
                    <FactoryForm />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              />

              {/* <Route
                exact
                path="/factorycreate"
                element={
                  user && user.approved ? (
                    <FactoryCreate isEdit={false} />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>
              <Route
                exact
                path="/factoryedit"
                element={
                  user && user.approved ? (
                    <FactoryCreate isEdit={true} />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route> */}
              <Route
                exact
                path="/private-accounts"
                element={
                  user && user.approved && user.domain ? (
                    <PrivateAccounts />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>
              <Route exact path="/uns" element={<Uns />}></Route>
              <Route
                exact
                path="/flows/:flowId"
                element={user ? <Flow /> : <Navigate to="/" replace={true} />}
              ></Route>
              <Route
                exact
                path="/manage-views"
                element={
                  user ? <ManageFilters /> : <Navigate to="/" replace={true} />
                }
              ></Route>
              <Route
                exact
                path="/asset/:account/:assetId"
                element={
                  user ? <AssetPage /> : <Navigate to="/" replace={true} />
                }
              ></Route>
              <Route exact path="/proveit" element={<Proveit />}></Route>
              <Route exact path="/proveit/sim" element={<ProveitSim />}></Route>
              <Route
                exact
                path="/proveit/edit"
                element={
                  user?.admin ? (
                    <ProveitEdit />
                  ) : (
                    <Navigate to="/proveit" replace={true} />
                  )
                }
              ></Route>
              <Route exact path="/modelscene" element={<ModelScene />}></Route>
              <Route
                exact
                path="/demo-instances"
                element={
                  user?.admin ? (
                    <Instances />
                  ) : (
                    <Navigate to="/" replace={true} />
                  )
                }
              ></Route>
            </Routes>
          </BrowserRouter>
        )}
      </Box>
      <Toast />
    </ThemeProvider>
  );
}

export default App;
